import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Refresh token every 8 hours
const REFRESH_INTERVAL_MS = 8 * 60 * 60 * 1000;

// Helper to get current position (GPS -> IP fallback via backend)
const getCurrentPosition = async () => {
  // 1) Try browser GPS with high accuracy and longer timeout
  if (navigator.geolocation) {
    const gpsResult = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ 
          latitude: position.coords.latitude, 
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy 
        }),
        () => resolve(null),
        { timeout: 15000, enableHighAccuracy: true, maximumAge: 30000 }
      );
    });
    if (gpsResult?.latitude) return gpsResult;
  }
  
  // 2) Fallback: IP geolocation via backend
  try {
    const resp = await axios.get(`${API}/geo/locate`, { timeout: 8000 });
    if (resp.data?.latitude && resp.data?.longitude) {
      return { latitude: resp.data.latitude, longitude: resp.data.longitude, accuracy: null };
    }
  } catch (e) {
    console.warn('[getCurrentPosition] IP fallback failed:', e.message);
  }
  
  return { latitude: null, longitude: null, accuracy: null };
};

// Pose le header Authorization de façon SYNCHRONE dès le chargement du module
// (avant tout rendu React). Sans cela, les requêtes émises par des composants
// montés avant le useEffect d'AuthProvider partaient SANS token → 403.
const _bootToken = localStorage.getItem('token');
if (_bootToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_bootToken}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Silent token refresh
  const refreshToken = useCallback(async (currentToken) => {
    if (isRefreshingRef.current || !currentToken) return null;
    isRefreshingRef.current = true;
    try {
      const resp = await axios.post(`${API}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${currentToken}` },
        _skipInterceptor: true
      });
      const newToken = resp.data.token;
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      return newToken;
    } catch {
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Setup auto-refresh interval
  useEffect(() => {
    if (!token) {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      return;
    }
    refreshTimerRef.current = setInterval(() => {
      const t = localStorage.getItem('token');
      if (t) refreshToken(t);
    }, REFRESH_INTERVAL_MS);
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
  }, [token, refreshToken]);

  // Axios 401 interceptor: try refresh before logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const _st = error.response?.status;
        if (
          (_st === 401 || _st === 403) &&
          !originalRequest._retry &&
          !originalRequest._skipInterceptor &&
          localStorage.getItem('token')
        ) {
          originalRequest._retry = true;
          if (_st === 403) {
            // 403 = header absent (course au chargement) → re-pose le header
            // depuis localStorage et retente une fois.
            const t = localStorage.getItem('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
            originalRequest.headers['Authorization'] = `Bearer ${t}`;
            return axios(originalRequest);
          }
          const newToken = await refreshToken(localStorage.getItem('token'));
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshToken]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Pose le cookie HttpOnly des médias (/api/uploads) — les balises
      // <img>/<video> ne peuvent pas envoyer le header Authorization.
      axios.post(`${API}/auth/cookie-sync`, null, {
        headers: { Authorization: `Bearer ${token}` },
        _skipInterceptor: true,
      }).catch(() => {});
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Sync multi-onglets : si le token change dans un autre onglet (ex. l'un
  // en session employé, l'autre en session Admin), aligner axios + state —
  // évite les 403 silencieux avec un token périmé.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token') return;
      if (e.newValue) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
        setToken(e.newValue);
      } else {
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Store userId for components that need it from localStorage
      localStorage.setItem('userId', response.data.id);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Request all permissions once after login (camera, geolocation)
  // Called within user gesture context (login button click) so browsers allow it
  const requestAllPermissions = async () => {
    // 1. Camera permission
    if (localStorage.getItem('camera-permission') !== 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        stream.getTracks().forEach(track => track.stop()); // Release immediately
        localStorage.setItem('camera-permission', 'granted');
        console.log('[Permissions] Camera granted');
      } catch (e) {
        console.log('[Permissions] Camera denied:', e.message);
        localStorage.setItem('camera-permission', 'denied');
      }
    }

    // 2. Microphone permission
    if (localStorage.getItem('microphone-permission') !== 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        stream.getTracks().forEach(track => track.stop());
        localStorage.setItem('microphone-permission', 'granted');
        console.log('[Permissions] Microphone granted');
      } catch (e) {
        console.log('[Permissions] Microphone denied:', e.message);
        localStorage.setItem('microphone-permission', 'denied');
      }
    }

    // 3. Notifications permission
    if (localStorage.getItem('notifications-permission') !== 'granted' && 'Notification' in window) {
      try {
        const result = await Notification.requestPermission();
        localStorage.setItem('notifications-permission', result);
        console.log('[Permissions] Notifications:', result);
      } catch (e) {
        console.log('[Permissions] Notifications error:', e.message);
      }
    }

    // 4. Geolocation permission
    if (localStorage.getItem('geolocation-consent-granted') !== 'true') {
      try {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => {
              localStorage.setItem('geolocation-consent-granted', 'true');
              console.log('[Permissions] Geolocation granted');
              resolve();
            },
            () => {
              console.log('[Permissions] Geolocation denied');
              resolve();
            },
            { timeout: 5000 }
          );
        });
      } catch (e) {
        console.log('[Permissions] Geolocation error:', e.message);
      }
    }
  };

  const login = async (email, password) => {
    // Get geolocation before login
    const position = await getCurrentPosition();
    
    const response = await axios.post(`${API}/auth/login`, { 
      email, 
      password,
      latitude: position.latitude,
      longitude: position.longitude
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userId', userData.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    // Request all permissions right after login (user gesture context)
    requestAllPermissions();
    
    return userData;
  };

  const register = async (email, password, name, role = 'employee') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userId', userData.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Get geolocation before logout and log it
    if (token && user?.role === 'employee') {
      try {
        const position = await getCurrentPosition();
        await axios.post(`${API}/auth/logout`, {
          latitude: position.latitude,
          longitude: position.longitude
        });
      } catch (error) {
        console.error('Logout log error:', error);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Set user and token directly (for dev login)
  const setUserAndToken = (userData, newToken) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('userId', userData.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    requestAllPermissions();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      register, 
      logout, 
      loading,
      setUserAndToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
